import React from 'react';
import type { ControllerFieldState, ControllerRenderProps, FieldPath } from 'react-hook-form';
import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  type RadioGroupProps,
} from '@mui/material';

import type { CMS1500FormData } from '../ClaimCMS1500Form';
import { useFieldErrorHelperTextStyling } from '../hooks/useFieldErrorStyling';

interface RadioOption {
  value: string | boolean;
  label: string;
  disabled?: boolean;
}

interface ClaimRadioGroupProps<TFieldName extends FieldPath<CMS1500FormData> = FieldPath<CMS1500FormData>> 
  extends Omit<RadioGroupProps, 'value' | 'onChange'> {
  /** Field props from react-hook-form Controller */
  field: ControllerRenderProps<CMS1500FormData, TFieldName>;
  /** Field state from react-hook-form Controller */
  fieldState?: ControllerFieldState;
  /** Radio button options */
  options: RadioOption[];
  /** Whether all options are disabled */
  disabled?: boolean;
  /** Override helper text (defaults to error message) */
  helperText?: string;
  /** Convert value for display (e.g., boolean to string) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  valueConverter?: (value: any) => string;
  /** Convert display value back (e.g., string to boolean) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChangeConverter?: (value: string) => any;
}

/**
 * ClaimRadioGroup - A RadioGroup wrapper that automatically applies severity-based error styling
 * 
 * This component handles:
 * - Error severity detection (error/warning/info)
 * - Helper text with appropriate color coding
 * - Boolean to string conversion for radio values
 * - Consistent styling across all claim form radio groups
 * 
 * @example
 * ```tsx
 * // Simple usage
 * <Controller
 *   name="patientSex"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <ClaimRadioGroup
 *       field={field}
 *       fieldState={fieldState}
 *       options={[
 *         { value: 'M', label: 'Male' },
 *         { value: 'F', label: 'Female' }
 *       ]}
 *       row
 *     />
 *   )}
 * />
 * 
 * // With boolean conversion
 * <Controller
 *   name="acceptAssignment"
 *   control={control}
 *   render={({ field, fieldState }) => (
 *     <ClaimRadioGroup
 *       field={field}
 *       fieldState={fieldState}
 *       options={[
 *         { value: true, label: 'YES' },
 *         { value: false, label: 'NO' }
 *       ]}
 *       valueConverter={(val) => val === true ? 'true' : val === false ? 'false' : ''}
 *       onChangeConverter={(val) => val === 'true' ? true : val === 'false' ? false : undefined}
 *       row
 *     />
 *   )}
 * />
 * ```
 */
export const ClaimRadioGroup = <TFieldName extends FieldPath<CMS1500FormData> = FieldPath<CMS1500FormData>>({
  field,
  fieldState,
  options,
  disabled,
  helperText,
  valueConverter,
  onChangeConverter,
  ...radioGroupProps
}: ClaimRadioGroupProps<TFieldName>): React.ReactElement => {
  const { helperTextSx } = useFieldErrorHelperTextStyling(fieldState);
  
  // Handle value conversion for display
  const displayValue = valueConverter 
    ? valueConverter(field.value) 
    : field.value != null 
      ? typeof field.value === 'object' 
        ? JSON.stringify(field.value) 
        : String(field.value)
      : '';
  
  // Handle change with optional conversion
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const convertedValue = onChangeConverter ? onChangeConverter(newValue) : newValue;
    field.onChange(convertedValue);
  };

  return (
    <Box>
      <RadioGroup
        {...radioGroupProps}
        value={displayValue}
        onChange={handleChange}
        onBlur={field.onBlur}
        name={field.name}
        ref={field.ref}
      >
        {options.map((option) => {
          const optionValue = typeof option.value === 'boolean' 
            ? option.value.toString() 
            : option.value;
          
          return (
            <FormControlLabel
              key={optionValue}
              value={optionValue}
              control={
                <Radio 
                  size="small" 
                  disabled={disabled || option.disabled} 
                />
              }
              label={option.label}
            />
          );
        })}
      </RadioGroup>
      {(fieldState?.error || helperText) && (
        <FormHelperText sx={helperTextSx}>
          {helperText ?? fieldState?.error?.message}
        </FormHelperText>
      )}
    </Box>
  );
};

ClaimRadioGroup.displayName = 'ClaimRadioGroup';